import React, { useEffect, useState } from 'react';
import Layout from '../components/shared/Layout';
import { holidayAPI } from '../services/api';
import toast from 'react-hot-toast';
import { useConfirm } from '../components/shared/ConfirmDialog';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { CalendarDays, Plus, Pencil, Trash2, X, RotateCcw, CheckCircle2 } from 'lucide-react';

const MONTHS = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const DAY_NAMES = ['Dom','Lun','Mar','Mié','Jue','Vie','Sáb'];

// Feriados predeterminados de Paraguay
const PY_DEFAULTS = [
  { date: '-01-01', name: 'Año Nuevo' },
  { date: '-03-01', name: 'Día de los Héroes' },
  { date: '-05-01', name: 'Día del Trabajador' },
  { date: '-05-14', name: 'Día de la Independencia Nacional' },
  { date: '-05-15', name: 'Día de la Independencia Nacional' },
  { date: '-06-12', name: 'Paz del Chaco' },
  { date: '-06-20', name: 'Día Nacional de la Constitución' },
  { date: '-08-15', name: 'Fundación de Asunción' },
  { date: '-09-29', name: 'Día de la Batalla de Boquerón' },
  { date: '-12-08', name: 'Virgen de Caacupé' },
  { date: '-12-25', name: 'Navidad' },
  { date: '-12-31', name: 'Víspera de Año Nuevo' },
];

function HolidayModal({ holiday, onClose, onDone }) {
  const isEdit = !!holiday?.id;
  const [form, setForm] = useState({
    date: holiday?.date?.split('T')[0] || '',
    name: holiday?.name || '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.date || !form.name.trim()) return toast.error('Fecha y nombre son requeridos');
    setLoading(true);
    try {
      if (isEdit) {
        await holidayAPI.update(holiday.id, form);
        toast.success('Feriado actualizado');
      } else {
        await holidayAPI.create(form);
        toast.success('Feriado agregado');
      }
      onDone(); onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al guardar');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <CalendarDays size={18} color="var(--primary)" />
            {isEdit ? 'Editar feriado' : 'Nuevo feriado'}
          </div>
          <button className="modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Fecha *</label>
              <input type="date" className="form-input" value={form.date}
                onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">Nombre del feriado *</label>
              <input className="form-input" placeholder="Ej: Día de la Independencia"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                required
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Agregar feriado'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function HolidaysPage() {
  const confirm  = useConfirm();
  const [holidays, setHolidays] = useState([]);
  const [year,     setYear]     = useState(new Date().getFullYear());
  const [modal,    setModal]    = useState(null); // null | 'create' | holiday object
  const [recalcResult, setRecalcResult] = useState(null);
  const [recalcLoading, setRecalcLoading] = useState(false);

  useEffect(() => { loadHolidays(); }, [year]);

  const loadHolidays = async () => {
    try {
      const res = await holidayAPI.getAll({ year });
      setHolidays(res.data);
    } catch { toast.error('Error al cargar feriados'); }
  };

  const handleDelete = async (h) => {
    const ok = await confirm({
      title: `¿Eliminar "${h.name}"?`,
      message: `Se eliminará el feriado del ${format(new Date(h.date), 'dd/MM/yyyy')}.`,
      variant: 'danger',
      confirmLabel: 'Sí, eliminar',
    });
    if (!ok) return;
    try {
      await holidayAPI.remove(h.id);
      toast.success('Feriado eliminado');
      loadHolidays();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al eliminar');
    }
  };

  const handleLoadDefaults = async () => {
    const ok = await confirm({
      title: `Cargar feriados ${year}`,
      message: `Se agregarán los feriados nacionales predeterminados para el año ${year}. Los que ya existan serán ignorados.`,
      variant: 'confirm',
      confirmLabel: 'Sí, cargar',
    });
    if (!ok) return;
    let added = 0;
    for (const h of PY_DEFAULTS) {
      try {
        await holidayAPI.create({ date: `${year}${h.date}`, name: h.name });
        added++;
      } catch {}
    }
    toast.success(`${added} feriado(s) agregado(s)`);
    loadHolidays();
  };

  const handleRecalculate = async () => {
    const ok = await confirm({
      title: 'Recalcular saldos de vacaciones',
      message: 'Se recalcularán los días de todas las vacaciones APROBADAS considerando los feriados activos. Los saldos de los empleados serán ajustados automáticamente.',
      variant: 'confirm',
      confirmLabel: 'Recalcular',
    });
    if (!ok) return;
    setRecalcLoading(true);
    setRecalcResult(null);
    try {
      const res = await holidayAPI.recalculate();
      setRecalcResult(res.data);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al recalcular');
    } finally {
      setRecalcLoading(false);
    }
  };

  // Agrupar por mes
  const byMonth = Array.from({ length: 12 }, (_, i) => ({
    month: i,
    label: MONTHS[i],
    items: holidays.filter(h => {
      const month = Number(h.date.split('-')[1]) - 1;
      return month === i;
    }),
  })).filter(m => m.items.length > 0);

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 1 + i);

  return (
    <Layout title="Días feriados">
      <div className="page-header">
        <div>
          <div className="page-title">Días feriados / Festivos</div>
          <div className="page-subtitle">
            {holidays.length} feriado{holidays.length !== 1 ? 's' : ''} registrado{holidays.length !== 1 ? 's' : ''} en {year}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" onClick={handleLoadDefaults}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <CalendarDays size={15} /> Cargar {year}
          </button>
          <button className="btn btn-secondary" onClick={handleRecalculate} disabled={recalcLoading}
            style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--primary)' }}>
            <RotateCcw size={15} /> {recalcLoading ? 'Recalculando…' : 'Recalcular saldos'}
          </button>
          <button className="btn btn-primary" onClick={() => setModal('create')}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Plus size={15} /> Nuevo feriado
          </button>
        </div>
      </div>

      {/* Selector de año */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <span style={{ fontSize: 13, color: 'var(--gray-500)', fontWeight: 500 }}>Año:</span>
        <div style={{ display: 'flex', gap: 6 }}>
          {years.map(y => (
            <button key={y}
              onClick={() => setYear(y)}
              style={{
                padding: '6px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
                fontFamily: 'var(--font)', fontSize: 14, fontWeight: 500,
                background: year === y ? 'var(--primary)' : 'var(--gray-100)',
                color: year === y ? 'white' : 'var(--gray-700)',
                transition: 'all 0.15s',
              }}>
              {y}
            </button>
          ))}
        </div>
      </div>

      {/* Resultado de recálculo */}
      {recalcResult && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px',
          background: 'var(--success-light)', border: '1px solid #bbf7d0',
          borderRadius: 10, marginBottom: 20,
        }}>
          <CheckCircle2 size={18} color="var(--success)" />
          <div>
            <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--success)' }}>{recalcResult.message}</div>
            {recalcResult.updated > 0 && (
              <div style={{ fontSize: 12, color: 'var(--success)', marginTop: 2 }}>
                {recalcResult.totalDiff > 0
                  ? `Se devolvieron ${recalcResult.totalDiff} día(s) a los saldos de los empleados`
                  : `Se ajustaron ${recalcResult.updated} solicitud(es)`}
              </div>
            )}
          </div>
          <button onClick={() => setRecalcResult(null)}
            style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--success)' }}>
            <X size={16} />
          </button>
        </div>
      )}

      {/* Lista de feriados */}
      {holidays.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <CalendarDays size={40} color="var(--gray-300)" style={{ margin: '0 auto 12px', display: 'block' }} />
            <div className="empty-state-title">Sin feriados para {year}</div>
            <div style={{ fontSize: 13 }}>Agrega feriados manualmente o usa el botón "Cargar {year}" para cargar los festivos predeterminados</div>
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
          {byMonth.map(({ month, label, items }) => (
            <div key={month} className="card">
              <div style={{
                fontWeight: 600, fontSize: 14, color: 'var(--primary)',
                marginBottom: 12, paddingBottom: 10,
                borderBottom: '1px solid var(--gray-100)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              }}>
                <span>{label}</span>
                <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--gray-400)' }}>
                  {items.length} día{items.length > 1 ? 's' : ''}
                </span>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {items.map(h => {
                  const [y, m, dNum] = h.date.split('T')[0].split('-').map(Number);
                  const d = new Date(y, m - 1, dNum);
                  const dayName = DAY_NAMES[d.getDay()];
                  const isWeekend = d.getDay() === 0 || d.getDay() === 6;
                  return (
                    <div key={h.id} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '8px 10px', borderRadius: 8,
                      background: h.is_active ? 'var(--primary-light)' : 'var(--gray-50)',
                      opacity: h.is_active ? 1 : 0.6,
                    }}>
                      {/* Día del mes */}
                      <div style={{
                        width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                        background: isWeekend ? 'var(--gray-200)' : 'var(--primary)',
                        display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center',
                      }}>
                        <span style={{ fontSize: 16, fontWeight: 700, color: isWeekend ? 'var(--gray-600)' : 'white', lineHeight: 1 }}>
                          {d.getDate()}
                        </span>
                        <span style={{ fontSize: 9, color: isWeekend ? 'var(--gray-500)' : 'rgba(255,255,255,0.8)', fontWeight: 500 }}>
                          {dayName}
                        </span>
                      </div>
                      {/* Nombre */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--gray-900)' }} className="truncate">
                          {h.name}
                        </div>
                        {!h.is_active && (
                          <div style={{ fontSize: 11, color: 'var(--gray-400)' }}>Inactivo</div>
                        )}
                      </div>
                      {/* Acciones */}
                      <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                        <button onClick={() => setModal(h)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--gray-400)', borderRadius: 6, display: 'flex' }}
                          title="Editar">
                          <Pencil size={14} />
                        </button>
                        <button onClick={() => handleDelete(h)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--gray-400)', borderRadius: 6, display: 'flex' }}
                          title="Eliminar">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {modal && (
        <HolidayModal
          holiday={modal === 'create' ? null : modal}
          onClose={() => setModal(null)}
          onDone={loadHolidays}
        />
      )}
    </Layout>
  );
}